import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { Interaction } from "@/types";

// `attachment_ids` NÃO faz parte de `Interaction`: não é coluna da tabela, e sim
// uma instrução pro backend adotar anexos órfãos dentro da mesma transação do
// salvar (ver api/src/lib/attachments.ts). Fica só no tipo do payload de escrita
// pra não poluir o tipo da entidade que a tela lê.
type ComAnexos = { attachment_ids?: string[] };

export function useInteractions(clientId?: string) {
  return useQuery({
    queryKey: ["interactions", clientId ?? "all"],
    queryFn: async () => {
      const params = clientId ? { client_id: clientId } : undefined;
      const { data } = await apiClient.get<Interaction[]>("/interactions", { params });
      return data;
    },
  });
}

// Busca todas as interações (todos os clientes) — usado pra sinalizar tasks
// pendentes na lista de Investors, sem precisar abrir cada cliente.
export const useAllInteractions = () => useInteractions(undefined);

export function useCreateInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<Interaction, "id" | "created_at" | "updated_at"> & ComAnexos) => {
      const { data } = await apiClient.post<Interaction>("/interactions", payload);
      return data;
    },
    // Invalida por prefixo "interactions" (não só a chave do client_id), pra
    // também atualizar a query "all" usada no flag de tasks pendentes da lista
    // de Investors.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interactions"] }),
  });
}

export function useUpdateInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<Interaction> & { id: string } & ComAnexos) => {
      const { data } = await apiClient.put<Interaction>(`/interactions/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interactions"] }),
  });
}

export function useDeleteInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/interactions/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interactions"] }),
  });
}
